/**
 * CommandPalette.tsx
 *
 * Spotlight-style global search palette — opens on ⌘K / Ctrl+K.
 *
 * Features:
 *  - Instant navigation items (all 40+ pages, filtered by name)
 *  - Live Supabase search across: profiles, tasks, KPIs, tickets, projects
 *  - Quick actions (New Task, New Ticket, etc.)
 *  - Recent pages (last 5, stored in localStorage)
 *  - Full keyboard navigation: ↑↓ to move, Enter to select, Esc to close
 *  - Results grouped by type with icons
 *  - 200 ms debounce on Supabase queries
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  X,
  LayoutDashboard,
  TrendingUp,
  Users,
  CalendarDays,
  CheckSquare,
  LifeBuoy,
  MessageSquare,
  BarChart3,
  Settings,
  Shield,
  DollarSign,
  Target,
  Clock,
  FileText,
  Briefcase,
  UserCheck,
  LogOut,
  GraduationCap,
  User,
  Hash,
  FolderOpen,
  Crown,
  ArrowRight,
  History,
  Zap,
  Building2,
  Inbox,
  Activity,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useCommandPalette } from '../../context/CommandPaletteContext';
import { useModules } from '../../context/ModuleContext';
import type { ModuleKey } from '../../types';

// ─── navigation page definitions ─────────────────────────────────────────────

interface NavItem {
  id:       string;
  label:    string;
  path:     string;
  group:    string;
  icon:     React.ReactNode;
  keywords: string;
}

const ALL_NAV_ITEMS: NavItem[] = [
  // Dashboard
  { id: 'dashboard',        label: 'Dashboard',             path: '/dashboard',                group: 'Pages', icon: <LayoutDashboard size={15} />, keywords: 'home overview' },
  // Performance
  { id: 'perf-goals',       label: 'Company Goals',         path: '/performance/goals',         group: 'Performance', icon: <Target size={15} />,        keywords: 'okrs objectives goals' },
  { id: 'perf-kpis',        label: 'KPI Tracker',           path: '/performance/kpis',          group: 'Performance', icon: <TrendingUp size={15} />,     keywords: 'kpi tracker metrics' },
  { id: 'perf-my-kpis',     label: 'My KPIs',               path: '/performance/my-kpis',       group: 'Performance', icon: <TrendingUp size={15} />,     keywords: 'my kpis personal' },
  { id: 'perf-checkin',     label: 'Weekly Check-in',       path: '/performance/checkin',       group: 'Performance', icon: <CheckSquare size={15} />,    keywords: 'checkin weekly standup' },
  { id: 'perf-reviews',     label: 'Performance Reviews',   path: '/performance/reviews',       group: 'Performance', icon: <TrendingUp size={15} />,     keywords: 'reviews appraisal' },
  { id: 'perf-reports',     label: 'Weekly KPI Report',     path: '/performance/reports',       group: 'Performance', icon: <FileText size={15} />,       keywords: 'weekly kpi report export' },
  // People
  { id: 'employees',        label: 'Employees',             path: '/people/employees',          group: 'People', icon: <Users size={15} />,          keywords: 'employees staff people' },
  { id: 'org-chart',        label: 'Org Chart',             path: '/people/org-chart',          group: 'People', icon: <Building2 size={15} />,       keywords: 'org chart structure hierarchy' },
  { id: 'recruitment',      label: 'Recruitment',           path: '/people/recruitment',        group: 'People', icon: <Briefcase size={15} />,       keywords: 'recruitment jobs candidates hiring' },
  { id: 'onboarding',       label: 'Onboarding',            path: '/people/onboarding',         group: 'People', icon: <UserCheck size={15} />,       keywords: 'onboarding new hire' },
  { id: 'offboarding',      label: 'Offboarding',           path: '/people/offboarding',        group: 'People', icon: <LogOut size={15} />,          keywords: 'offboarding exit departure' },
  { id: 'training',         label: 'Training & Development', path: '/people/training',          group: 'People', icon: <GraduationCap size={15} />,   keywords: 'training courses learning development' },
  // HR
  { id: 'my-leave',         label: 'My Leave',              path: '/hr/my-leave',               group: 'HR', icon: <CalendarDays size={15} />,   keywords: 'leave time off vacation' },
  { id: 'team-leave',       label: 'Team Leave',            path: '/hr/team-leave',             group: 'HR', icon: <Users size={15} />,          keywords: 'team leave approval' },
  { id: 'leave-calendar',   label: 'Leave Calendar',        path: '/hr/calendar',               group: 'HR', icon: <CalendarDays size={15} />,   keywords: 'leave calendar' },
  { id: 'attendance',       label: 'Attendance',            path: '/hr/attendance',             group: 'HR', icon: <Clock size={15} />,          keywords: 'attendance check in out' },
  { id: 'documents',        label: 'Documents',             path: '/hr/documents',              group: 'HR', icon: <FileText size={15} />,       keywords: 'documents files' },
  { id: 'compliance',       label: 'Compliance',            path: '/hr/compliance',             group: 'HR', icon: <Shield size={15} />,         keywords: 'compliance alerts' },
  // Work
  { id: 'my-tasks',         label: 'My Tasks',              path: '/work/my-tasks',             group: 'Work', icon: <CheckSquare size={15} />,    keywords: 'tasks todo' },
  { id: 'projects',         label: 'Projects',              path: '/work/projects',             group: 'Work', icon: <FolderOpen size={15} />,      keywords: 'projects' },
  { id: 'time-tracking',    label: 'Time Tracking',         path: '/work/time-tracking',        group: 'Work', icon: <Clock size={15} />,           keywords: 'time tracking hours' },
  // Support
  { id: 'my-tickets',       label: 'My Tickets',            path: '/support/my-tickets',        group: 'Support', icon: <LifeBuoy size={15} />,       keywords: 'tickets support help' },
  { id: 'team-queue',       label: 'Team Queue',            path: '/support/team-queue',        group: 'Support', icon: <LifeBuoy size={15} />,       keywords: 'queue team support' },
  { id: 'all-tickets',      label: 'All Tickets',           path: '/support/all-tickets',       group: 'Support', icon: <LifeBuoy size={15} />,       keywords: 'all tickets admin' },
  { id: 'sla-dashboard',    label: 'SLA Dashboard',         path: '/support/sla',               group: 'Support', icon: <BarChart3 size={15} />,      keywords: 'sla dashboard metrics' },
  { id: 'knowledge-base',   label: 'Knowledge Base',        path: '/support/knowledge-base',    group: 'Support', icon: <FileText size={15} />,       keywords: 'knowledge base articles' },
  // Communication
  { id: 'channels',         label: 'Channels',              path: '/communication/channels',    group: 'Communication', icon: <Hash size={15} />,           keywords: 'channels slack messages' },
  { id: 'direct-messages',  label: 'Direct Messages',       path: '/communication/messages',    group: 'Communication', icon: <MessageSquare size={15} />,  keywords: 'dm direct messages chat' },
  // CRM
  { id: 'crm',              label: 'CRM Dashboard',         path: '/crm',                       group: 'CRM', icon: <BarChart3 size={15} />,       keywords: 'crm dashboard' },
  { id: 'crm-contacts',     label: 'Contacts',              path: '/crm/contacts',              group: 'CRM', icon: <User size={15} />,            keywords: 'crm contacts customers' },
  { id: 'crm-deals',        label: 'Deals',                 path: '/crm/deals',                 group: 'CRM', icon: <DollarSign size={15} />,      keywords: 'crm deals pipeline' },
  { id: 'crm-reports',      label: 'Sales Reports',         path: '/crm/reports',               group: 'CRM', icon: <BarChart3 size={15} />,       keywords: 'crm sales reports analytics pipeline forecast leaderboard' },
  // Finance
  { id: 'expenses',         label: 'Expenses',              path: '/finance/expenses',          group: 'Finance', icon: <DollarSign size={15} />,     keywords: 'expenses finance' },
  { id: 'budgets',          label: 'Budgets',               path: '/finance/budgets',           group: 'Finance', icon: <DollarSign size={15} />,     keywords: 'budgets finance' },
  { id: 'finance-reports',  label: 'Finance Reports',       path: '/finance/reports',           group: 'Finance', icon: <BarChart3 size={15} />,      keywords: 'finance reports' },
  // Analytics
  { id: 'analytics',        label: 'Analytics Overview',    path: '/analytics',                 group: 'Analytics', icon: <BarChart3 size={15} />,     keywords: 'analytics overview charts' },
  { id: 'custom-reports',   label: 'Custom Reports',        path: '/analytics/custom-reports',  group: 'Analytics', icon: <FileText size={15} />,      keywords: 'custom reports builder' },
  // Admin
  { id: 'admin-users',      label: 'Users',                 path: '/admin/users',               group: 'Admin', icon: <Users size={15} />,           keywords: 'users admin manage' },
  { id: 'admin-company',    label: 'Company Settings',      path: '/admin/company',             group: 'Admin', icon: <Building2 size={15} />,        keywords: 'company settings' },
  { id: 'admin-depts',      label: 'Departments & Teams',   path: '/admin/departments',         group: 'Admin', icon: <Building2 size={15} />,        keywords: 'departments teams' },
  { id: 'admin-holidays',   label: 'Holiday Management',    path: '/admin/holidays',            group: 'Admin', icon: <CalendarDays size={15} />,     keywords: 'holidays public' },
  { id: 'admin-leave-bal',  label: 'Leave Balances',        path: '/admin/leave-balances',      group: 'Admin', icon: <CalendarDays size={15} />,     keywords: 'leave balances admin' },
  { id: 'admin-policies',   label: 'Leave Policies',        path: '/admin/leave-policies',      group: 'Admin', icon: <Shield size={15} />,           keywords: 'leave policies rules' },
  { id: 'admin-billing',    label: 'Billing',               path: '/admin/billing',             group: 'Admin', icon: <DollarSign size={15} />,       keywords: 'billing subscription plan' },
  { id: 'admin-audit',      label: 'Audit Log',             path: '/admin/audit-log',           group: 'Admin', icon: <Shield size={15} />,           keywords: 'audit log history' },
  { id: 'admin-setup',      label: 'Setup Checklist',       path: '/admin/setup',               group: 'Admin', icon: <CheckSquare size={15} />,      keywords: 'setup checklist onboarding' },
  { id: 'settings',         label: 'Settings',              path: '/settings',                  group: 'Account', icon: <Settings size={15} />,       keywords: 'settings preferences account' },
  { id: 'notifications',    label: 'Notifications Inbox',   path: '/notifications',             group: 'Account', icon: <Inbox size={15} />,          keywords: 'notifications inbox alerts unread bell' },
  { id: 'privacy',          label: 'Privacy Centre',        path: '/settings/privacy',          group: 'Account', icon: <Shield size={15} />,         keywords: 'privacy gdpr data' },
  // Owner
  { id: 'owner-dashboard',  label: 'Owner Dashboard',       path: '/owner/dashboard',           group: 'Owner', icon: <Crown size={15} />,           keywords: 'owner saas dashboard' },
];

// ─── quick action definitions ─────────────────────────────────────────────────

interface QuickAction {
  id:     string;
  label:  string;
  hint:   string;
  /** Route to navigate to. `?new=1` paths auto-open the page's create modal —
   *  preserving the behaviour of the (now removed) bottom-left QuickActions FAB. */
  path:   string;
  icon:   React.ReactNode;
  /** Module gate — undefined = always shown (mirrors the old FAB's gating). */
  module?: ModuleKey;
}

// Create/jump actions. Absorbs the former bottom-left "+" QuickActions FAB so its
// capabilities live in one place ("+ New" / ⌘K). `?new=1` opens each page's
// create modal; module-gated entries hide when their module is disabled.
const QUICK_ACTIONS: QuickAction[] = [
  { id: 'qa-task',      label: 'New Task',           hint: 'Create a task',             path: '/work/my-tasks?new=1',      icon: <CheckSquare size={15} className="text-teal-400" />,   module: 'projects' },
  { id: 'qa-lead',      label: 'New Lead',           hint: 'Add a CRM lead',            path: '/crm/leads?new=1',          icon: <Briefcase size={15} className="text-violet-400" /> },
  { id: 'qa-deal',      label: 'New Deal',           hint: 'Open a deal',               path: '/crm/deals?new=1',          icon: <DollarSign size={15} className="text-amber-400" /> },
  { id: 'qa-activity',  label: 'Log Activity',       hint: 'Record a CRM activity',     path: '/crm/activities?new=1',     icon: <Activity size={15} className="text-pink-400" /> },
  { id: 'qa-expense',   label: 'Submit Expense',     hint: 'File an expense claim',     path: '/finance/expenses?new=1',   icon: <DollarSign size={15} className="text-green-400" />,    module: 'finance' },
  { id: 'qa-leave',     label: 'Request Leave',      hint: 'Submit a leave request',    path: '/hr/my-leave?new=1',        icon: <CalendarDays size={15} className="text-purple-400" />, module: 'hr' },
  { id: 'qa-ticket',    label: 'New Support Ticket', hint: 'Raise a support ticket',    path: '/support/my-tickets?new=1', icon: <LifeBuoy size={15} className="text-blue-400" />,       module: 'support' },
  { id: 'qa-employee',  label: 'View Employees',     hint: 'Browse the team directory', path: '/people/employees',         icon: <Users size={15} className="text-green-400" /> },
  { id: 'qa-settings',  label: 'Open Settings',      hint: 'Account and preferences',   path: '/settings',                 icon: <Settings size={15} className="text-white/50" /> },
];

// ─── localStorage recent pages ────────────────────────────────────────────────

const RECENT_KEY = 'pulsehq_recent_pages';
const MAX_RECENT = 5;

function getRecent(): { path: string; label: string }[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch {
    return [];
  }
}

export function recordRecentPage(path: string, label: string) {
  const recent = getRecent().filter(r => r.path !== path);
  recent.unshift({ path, label });
  localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

// ─── result types ─────────────────────────────────────────────────────────────

type ResultGroup =
  | 'Recent'
  | 'Quick Actions'
  | 'Pages'
  | 'Performance'
  | 'People'
  | 'HR'
  | 'Work'
  | 'Support'
  | 'Communication'
  | 'CRM'
  | 'Finance'
  | 'Analytics'
  | 'Admin'
  | 'Account'
  | 'Owner'
  | 'Employees'
  | 'Tasks'
  | 'KPIs'
  | 'Tickets';

interface PaletteResult {
  id:       string;
  group:    ResultGroup;
  label:    string;
  subtitle?: string;
  path:     string;
  icon:     React.ReactNode;
}

// ─── group display order ──────────────────────────────────────────────────────

const GROUP_ORDER: ResultGroup[] = [
  'Recent', 'Quick Actions',
  'Employees', 'Tasks', 'KPIs', 'Tickets',
  'Pages', 'Performance', 'People', 'HR', 'Work', 'Support',
  'Communication', 'CRM', 'Finance', 'Analytics', 'Admin', 'Account', 'Owner',
];

function groupOrder(g: ResultGroup) {
  const idx = GROUP_ORDER.indexOf(g);
  return idx === -1 ? 99 : idx;
}

// ─── component ────────────────────────────────────────────────────────────────

export default function CommandPalette() {
  const { isOpen, close } = useCommandPalette();
  const { company, isEnabled } = useModules();
  const companyId         = (company as any)?.id as string | undefined;
  const navigate          = useNavigate();

  const [query,     setQuery]     = useState('');
  const [results,   setResults]   = useState<PaletteResult[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [searching, setSearching] = useState(false);

  const inputRef    = useRef<HTMLInputElement>(null);
  const listRef     = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus input when palette opens; reset on close
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [isOpen]);

  // Build the empty-state results (recent + quick actions)
  const emptyResults = useMemo<PaletteResult[]>(() => {
    const recent = getRecent().map(r => {
      const navItem = ALL_NAV_ITEMS.find(n => n.path === r.path);
      return {
        id:    `recent-${r.path}`,
        group: 'Recent' as ResultGroup,
        label: r.label,
        path:  r.path,
        icon:  navItem?.icon ?? <History size={15} />,
      };
    });
    const actions = QUICK_ACTIONS
      .filter(a => !a.module || isEnabled(a.module))   // hide actions for disabled modules
      .map(a => ({
        id:       a.id,
        group:    'Quick Actions' as ResultGroup,
        label:    a.label,
        subtitle: a.hint,
        path:     a.path,
        icon:     a.icon,
      }));
    return [...recent, ...actions];
    // `isOpen` is in deps to force recompute when the palette opens — the
    // callback body doesn't read it, but we want the recent-items refresh
    // to happen each time the user opens the palette.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEnabled]);

  // Search function (Supabase + nav filter)
  const runSearch = useCallback(async (q: string) => {
    const lq = q.toLowerCase().trim();

    // ── Nav items ──────────────────────────────────────────────────────────────
    const navResults: PaletteResult[] = ALL_NAV_ITEMS
      .filter(n =>
        n.label.toLowerCase().includes(lq) ||
        n.keywords.toLowerCase().includes(lq),
      )
      .slice(0, 8)
      .map(n => ({
        id:    `nav-${n.id}`,
        group: n.group as ResultGroup,
        label: n.label,
        path:  n.path,
        icon:  n.icon,
      }));

    setResults(navResults);
    setSearching(true);

    // ── Supabase live searches ─────────────────────────────────────────────────
    // eslint-disable-next-line no-restricted-syntax -- Promise.all results: error handling verified in surrounding code
    // eslint-disable-next-line no-restricted-syntax -- Promise.all results: error handling verified in surrounding code
    // eslint-disable-next-line no-restricted-syntax -- Promise.all results: error handling verified in surrounding code
    // eslint-disable-next-line no-restricted-syntax -- Promise.all results: error handling verified in surrounding code
    const [profileRes, taskRes, kpiRes, ticketRes] = await Promise.all([
      supabase
        .from('profiles')
        .select('id,full_name,role,email')
        .ilike('full_name', `%${q}%`)
        .limit(4),
      companyId
        ? supabase
            .from('tasks')
            .select('id,title,status')
            .eq('is_deleted', false)          // 0147 — an archived task is not findable
            .ilike('title', `%${q}%`)
            .limit(4)
        : Promise.resolve({ data: [] }),
      companyId
        ? supabase
            .from('kpis')
            .select('id,name,status')
            .ilike('name', `%${q}%`)
            .limit(4)
        : Promise.resolve({ data: [] }),
      companyId
        ? supabase
            .from('support_tickets')
            .select('id,title,status')
            .ilike('title', `%${q}%`)
            .limit(4)
        : Promise.resolve({ data: [] }),
    ]);

    const empResults: PaletteResult[] = (profileRes.data || []).map((p: any) => ({
      id:       `emp-${p.id}`,
      group:    'Employees' as ResultGroup,
      label:    p.full_name,
      subtitle: p.role?.replace('_', ' ') + ' · ' + p.email,
      path:     '/people/employees',
      icon:     <User size={15} className="text-teal-400" />,
    }));

    const taskResults: PaletteResult[] = (taskRes.data || []).map((t: any) => ({
      id:       `task-${t.id}`,
      group:    'Tasks' as ResultGroup,
      label:    t.title,
      subtitle: `Status: ${t.status}`,
      path:     '/work/my-tasks',
      icon:     <CheckSquare size={15} className="text-blue-400" />,
    }));

    const kpiResults: PaletteResult[] = (kpiRes.data || []).map((k: any) => ({
      id:       `kpi-${k.id}`,
      group:    'KPIs' as ResultGroup,
      label:    k.name,
      subtitle: `Status: ${k.status?.replace('_', ' ')}`,
      path:     '/performance/kpis',
      icon:     <TrendingUp size={15} className="text-purple-400" />,
    }));

    const ticketResults: PaletteResult[] = (ticketRes.data || []).map((t: any) => ({
      id:       `ticket-${t.id}`,
      group:    'Tickets' as ResultGroup,
      label:    t.title,
      subtitle: `Status: ${t.status}`,
      path:     '/support/my-tickets',
      icon:     <LifeBuoy size={15} className="text-orange-400" />,
    }));

    setSearching(false);
    setResults([
      ...navResults,
      ...empResults,
      ...taskResults,
      ...kpiResults,
      ...ticketResults,
    ]);
    setActiveIdx(0);
  }, [companyId]);

  // Debounced search trigger
  useEffect(() => {
    if (!isOpen) return;
    if (!query.trim()) {
      setResults([]);
      setSearching(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, isOpen, runSearch]);

  // Visible results (either search results or empty-state)
  const visible = query.trim() ? results : emptyResults;

  // Flat ordered list for keyboard nav
  const flat = useMemo(() => {
    const grouped = new Map<ResultGroup, PaletteResult[]>();
    for (const r of visible) {
      if (!grouped.has(r.group)) grouped.set(r.group, []);
      grouped.get(r.group)!.push(r);
    }
    const sorted = Array.from(grouped.entries()).sort(([a], [b]) => groupOrder(a) - groupOrder(b));
    return sorted.flatMap(([, items]) => items);
  }, [visible]);

  // Navigate to a result
  const select = useCallback((result: PaletteResult) => {
    // Don't record create-actions (?new=1) as "recent pages" — re-opening a
    // create modal isn't a navigation destination.
    if (!result.path.includes('?new=1')) recordRecentPage(result.path, result.label);
    close();
    navigate(result.path);
  }, [close, navigate]);

  // Keyboard handler
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { close(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && flat[activeIdx]) {
      select(flat[activeIdx]);
    }
  }, [flat, activeIdx, close, select]);

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${activeIdx}"]`);
    (el as HTMLElement | null)?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  if (!isOpen) return null;

  // Build grouped display
  const groupedMap = new Map<ResultGroup, PaletteResult[]>();
  for (const r of visible) {
    if (!groupedMap.has(r.group)) groupedMap.set(r.group, []);
    groupedMap.get(r.group)!.push(r);
  }
  const groupedEntries = Array.from(groupedMap.entries()).sort(([a], [b]) => groupOrder(a) - groupOrder(b));

  // Running flat index for active state comparison
  let flatIdx = 0;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50"
        style={{ background: 'rgba(0,0,0,0.60)', backdropFilter: 'blur(4px)' }}
        onClick={close}
      />

      {/* Palette panel */}
      <div
        className="fixed top-24 left-1/2 -translate-x-1/2 z-50 w-full"
        style={{ maxWidth: '640px', padding: '0 16px' }}
        onKeyDown={handleKeyDown}
      >
        <div
          className="rounded-2xl overflow-hidden shadow-2xl"
          style={{
            background: 'rgba(10,15,30,0.97)',
            border: '1px solid rgba(255,255,255,0.12)',
            backdropFilter: 'blur(20px)',
          }}
        >
          {/* Search input row */}
          <div className="flex items-center gap-3 px-4 py-3.5 border-b border-white/10">
            <Search size={18} className="text-white/40 flex-shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search pages, people, tasks, KPIs…"
              className="flex-1 bg-transparent text-white placeholder-white/30 text-sm focus:outline-none"
              spellCheck={false}
              autoComplete="off"
            />
            {searching && (
              <div className="w-3.5 h-3.5 border-2 border-teal-400/40 border-t-teal-400 rounded-full animate-spin flex-shrink-0" />
            )}
            <button
              onClick={close}
              className="p-1 text-white/30 hover:text-white transition-colors flex-shrink-0"
            >
              <X size={16} />
            </button>
          </div>

          {/* Results */}
          <div
            ref={listRef}
            className="overflow-y-auto"
            style={{ maxHeight: '420px' }}
          >
            {flat.length === 0 && !searching && query.trim() ? (
              <div className="py-12 text-center text-white/30 text-sm">
                No results for "{query}"
              </div>
            ) : flat.length === 0 && !query.trim() ? null : (
              <div className="py-2">
                {groupedEntries.map(([group, items]) => (
                  <div key={group}>
                    {/* Group header */}
                    <div className="px-4 py-1.5 flex items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wider text-white/30">
                        {group}
                      </span>
                      {group === 'Recent' && <History size={10} className="text-white/20" />}
                      {group === 'Quick Actions' && <Zap size={10} className="text-white/20" />}
                    </div>

                    {/* Items */}
                    {items.map(result => {
                      const myIdx = flatIdx++;
                      const isActive = myIdx === activeIdx;
                      return (
                        <button
                          key={result.id}
                          data-idx={myIdx}
                          onClick={() => select(result)}
                          onMouseEnter={() => setActiveIdx(myIdx)}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors text-left ${
                            isActive ? 'bg-teal-500/20' : 'hover:bg-white/10/5'
                          }`}
                        >
                          {/* Icon */}
                          <span className={`flex-shrink-0 ${isActive ? 'text-teal-400' : 'text-white/40'}`}>
                            {result.icon}
                          </span>

                          {/* Label + subtitle */}
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium leading-tight ${isActive ? 'text-white' : 'text-white/80'}`}>
                              {result.label}
                            </p>
                            {result.subtitle && (
                              <p className="text-xs text-white/40 mt-0.5 truncate">{result.subtitle}</p>
                            )}
                          </div>

                          {/* Arrow on active */}
                          {isActive && (
                            <ArrowRight size={13} className="text-teal-400 flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer hints */}
          <div className="px-4 py-2.5 border-t border-white/10 flex items-center gap-4 text-xs text-white/25">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white/40 font-mono text-xs">↑↓</kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white/40 font-mono text-xs">↵</kbd>
              select
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white/40 font-mono text-xs">esc</kbd>
              close
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
